import { DateTime, Duration } from 'luxon';
import { HourReadingParsers } from '../hour-reading/hour-reading-parsers';
import {
  CalculateOverageChargeRequest,
  CalculateOverageChargeResult,
  Make,
  Model,
  Product,
  ProductDepreciationPercentage,
  ProductInventoryUnit,
  ProductLocationStock,
  ProductTrackingType,
  ProductType,
} from './product';

export namespace ProductParsers {
  export class ProductJson {
    id!: number;
    name!: string;
    isActive!: boolean;
    isDeleted!: boolean;
    allowRent!: boolean;
    allowSale!: boolean;
    trackingType!: ProductTrackingType;
    minRentalDuration!: string;
    maxRentalDuration?: string;
    locationIds!: number[];
    categoryIds!: number[];
    rentalStock!: number;
    saleStock!: number;
    inventoryUnits!: ProductInventoryUnitJson[];
    disabledEndDate?: string;
    disabledStartDate?: string;
    allowHourTracking!: boolean;
    fuelChargeProductId?: number;
    purchasePrice?: number;
    outageStock!: number;
    createdAt!: string;
    taxable!: boolean;
    sku?: string;
    stock?: ProductLocationStock[];
    type!: ProductType;
    seisRdw?: boolean;
  }

  export class ProductLocationStockJson {
    productId!: number;
    locationId!: number;
    rentalStock!: number;
    saleStock!: number;
  }

  export type ProductInventoryUnitJson = {
    id: number;
    productId: number;
    locationId: number;
    unitNumber: string;
    serialNumber: string;
    qrCode?: string;
    purchasePrice?: number;
    isOnOutage: boolean;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    deletedAt?: string;
    lastHourReading?: HourReadingParsers.HourReadingJson;
    classification?: ProductInventoryUnitTaxonomyClassificationJson;
    forRent: boolean;
    forSale: boolean;
    purchasedAt: string;
    soldAt?: string;
    boomAndBucketEquipmentId?: string;
  };

  export class ProductInventoryUnitTaxonomyClassificationJson {
    make!: Make;
    model!: Model;
    year!: number;
  }

  export class ProductDepreciationPercentageJson {
    id!: number;
    productId!: number;
    depreciationPercentage!: number;
    createdAt!: DateTime;
  }

  export function toProductInventoryUnit(input: ProductInventoryUnitJson): ProductInventoryUnit {
    return {
      id: input.id,
      productId: input.productId,
      locationId: input.locationId,
      unitNumber: input.unitNumber,
      serialNumber: input.serialNumber,
      qrCode: input.qrCode,
      purchasePrice: input.purchasePrice,
      isOnOutage: input.isOnOutage,
      isActive: input.isActive,
      createdAt: DateTime.fromISO(input.createdAt, { setZone: true }),
      updatedAt: DateTime.fromISO(input.updatedAt, { setZone: true }),
      deletedAt: input.deletedAt ? DateTime.fromISO(input.deletedAt, { setZone: true }) : undefined,
      lastHourReading: input.lastHourReading && HourReadingParsers.toHourReadng(input.lastHourReading),
      classification: input.classification,
      forRent: input.forRent,
      forSale: input.forSale,
      purchasedAt: DateTime.fromISO(input.purchasedAt, { setZone: true }),
      soldAt: input.soldAt ? DateTime.fromISO(input.soldAt, { setZone: true }) : undefined,
      boomAndBucketEquipmentId: input.boomAndBucketEquipmentId,
    };
  }

  export function toProduct(input: ProductJson): Product {
    return {
      id: input.id,
      name: input.name,
      isActive: input.isActive,
      isDeleted: input.isDeleted,
      allowRent: input.allowRent,
      allowSale: input.allowSale,
      trackingType: input.trackingType,
      minRentalDuration: Duration.fromISO(input.minRentalDuration),
      maxRentalDuration: input.maxRentalDuration ? Duration.fromISO(input.maxRentalDuration) : undefined,
      locationIds: input.locationIds,
      categoryIds: input.categoryIds,
      rentalStock: input.rentalStock,
      saleStock: input.saleStock,
      inventoryUnits: input.inventoryUnits.map((iu) => toProductInventoryUnit(iu)),
      disabledEndDate: input.disabledEndDate ? DateTime.fromISO(input.disabledEndDate, { setZone: true }) : undefined,
      disabledStartDate: input.disabledStartDate
        ? DateTime.fromISO(input.disabledStartDate, { setZone: true })
        : undefined,
      allowHourTracking: input.allowHourTracking,
      fuelChargeProductId: input.fuelChargeProductId,
      purchasePrice: input.purchasePrice,
      outageStock: input.outageStock,
      createdAt: DateTime.fromISO(input.createdAt, { setZone: true }),
      taxable: input.taxable,
      sku: input.sku,
      stock: input.stock,
      type: input.type,
      seisRdw: input.seisRdw,
    };
  }

  export function toProductLocationStock(input: ProductLocationStockJson): ProductLocationStock {
    return {
      productId: input.productId,
      locationId: input.locationId,
      rentalStock: input.rentalStock,
      saleStock: input.saleStock,
    };
  }

  export type CalculateOverageChargeRequestJson = {
    orderId: number;
    orderProductId: number;
    unitId: number;
    start: string;
    end: string;
    hoursIn: number;
  };

  export function toCalculateOverageChargeRequest(
    input: CalculateOverageChargeRequestJson,
  ): CalculateOverageChargeRequest {
    return {
      orderId: input.orderId,
      orderProductId: input.orderProductId,
      unitId: input.unitId,
      start: DateTime.fromISO(input.start, { setZone: true }),
      end: DateTime.fromISO(input.end, { setZone: true }),
      hoursIn: input.hoursIn,
    };
  }

  export type CalculateOverageChargeResultJson = CalculateOverageChargeRequestJson & {
    dailyHoursLimit: number;
    hourlyOverageRate: number;
    overageHours: number;
    overageCharge: number;
  };

  export function toCalculateOverageChargeResult(
    input: CalculateOverageChargeResultJson,
  ): CalculateOverageChargeResult {
    return {
      ...toCalculateOverageChargeRequest(input),
      dailyHoursLimit: input.dailyHoursLimit,
      hourlyOverageRate: input.hourlyOverageRate,
      overageHours: input.overageHours,
      overageCharge: input.overageCharge,
    };
  }

  export function toProductDepreciationPercentage(
    input: ProductDepreciationPercentageJson,
  ): ProductDepreciationPercentage {
    return {
      id: input.id,
      productId: input.productId,
      depreciationPercentage: input.depreciationPercentage,
      createdAt: input.createdAt,
    };
  }
}
